import React, { useEffect, useRef } from 'react'
import { Animated, Easing, StyleProp, View, ViewStyle } from 'react-native'
import { Circle, Svg } from 'react-native-svg'
import { useTheme } from '@src/config/theme'
import NS from '@src/assets/svgs/ns'

const AnimatedView = Animated.createAnimatedComponent(View)

export interface NSLoadingIndicatorProps {
  size?: number
  indicatorSize?: number
  strokeWidth?: number
  disableAnimation?: boolean
  backgroundColor?: string
  indicatorColor?: string
  logoColor?: string
  style?: StyleProp<ViewStyle>
  testID?: string
}

const DEFAULT_SIZE = 100
const DEFAULT_STROKE_WIDTH = 5
const ROTATION_DURATION_MS = 2500
const ARC_LENGTH_RATIO = 0.2
const ARC_START_ANGLE = -90

export const NSLoadingIndicator = ({
  size = DEFAULT_SIZE,
  indicatorSize,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  disableAnimation = false,
  backgroundColor,
  indicatorColor,
  logoColor,
  style,
  testID,
}: NSLoadingIndicatorProps) => {
  const { theme, themeName } = useTheme()
  const rotate = useRef(new Animated.Value(0)).current
  const resolvedIndicatorSize = indicatorSize || size

  const logoSize = resolvedIndicatorSize * 0.6

  useEffect(() => {
    rotate.setValue(0)

    if (disableAnimation) {
      return
    }

    const animation = Animated.loop(
      Animated.timing(rotate, {
        toValue: 1,
        duration: ROTATION_DURATION_MS,
        easing: Easing.linear,
        isInteraction: false,
        useNativeDriver: false,
      }),
      { resetBeforeIteration: true },
    )

    animation.start()

    return () => {
      animation.stop()
      rotate.setValue(0)
    }
  }, [disableAnimation, rotate])

  const resolvedBackgroundColor = backgroundColor || theme[`palette.${themeName}.surface.base1`]
  const resolvedIndicatorColor = indicatorColor || theme[`palette.${themeName}.surface.base0`]
  const resolvedLogoColor = logoColor || theme[`palette.${themeName}.text.primary`]

  const radius = (resolvedIndicatorSize - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const arcLength = circumference * ARC_LENGTH_RATIO
  const gapLength = circumference - arcLength
  const rotation = rotate.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  })

  return (
    <View
      testID={testID}
      style={[
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: resolvedBackgroundColor,
        },
        style,
      ]}
    >
      <AnimatedView
        style={{
          position: 'absolute',
          width: resolvedIndicatorSize,
          height: resolvedIndicatorSize,
          transform: [{ rotate: rotation }],
        }}
      >
        <Svg
          width={resolvedIndicatorSize}
          height={resolvedIndicatorSize}
          viewBox={`0 0 ${resolvedIndicatorSize} ${resolvedIndicatorSize}`}
        >
          <Circle
            cx={resolvedIndicatorSize / 2}
            cy={resolvedIndicatorSize / 2}
            r={radius}
            fill="none"
            stroke={resolvedIndicatorColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${gapLength}`}
            strokeLinecap="round"
            transform={`rotate(${ARC_START_ANGLE} ${resolvedIndicatorSize / 2} ${
              resolvedIndicatorSize / 2
            })`}
          />
        </Svg>
      </AnimatedView>

      <NS width={logoSize} height={logoSize} color={resolvedLogoColor} />
    </View>
  )
}
